/**
 * SlideCanvas — CON-1610. The ONE slide renderer.
 *
 * 🔴 WHY FIXED 1920×1080 + transform:scale, and not responsive CSS.
 * This component draws the editor thumbnail, the editor preview, the audience
 * screen, the rehearsal mini-slide and the PDF export. If those five used
 * responsive rules they would disagree — a headline that wraps at 380px and not
 * at 1920px means the preview lies, and a preview that lies is worse than no
 * preview. So the slide is ALWAYS laid out at exactly 1920×1080 and then scaled
 * as a single unit. Every line break happens in the same place everywhere,
 * including on paper.
 *
 * Type sizes come from the theme as PERCENT OF SLIDE WIDTH. The organisers'
 * floor is 24pt with "32-45pt your smallest range"; on a 1920-wide 16:9 deck
 * 2.2% ≈ 42px ≈ 32pt, so no token in any shipped theme goes below 2.2.
 */
import React, { useMemo } from 'react';
import { QRCodeSVG } from 'qrcode.react';

const W = 1920, H = 1080;

const DEFAULT_TOKENS = {
  bg: '#0f172a', bgAlt: '#111827', ink: '#f8fafc', inkSoft: '#cbd5e1', inkFaint: '#7c8ba1',
  accent: '#E46E34', accent2: '#f0a06a', line: '#243044', qrBg: '#ffffff', qrInk: '#0f172a',
  // 🔴 BRAND FONTS, and they are actually LOADED (see index.html). These were
  // 'Inter' — which nothing ever loaded, so the deck silently rendered in the
  // viewer's OS fallback and broke line-for-line between machines.
  headFont: "'Baloo 2','Hanken Grotesk','Segoe UI',system-ui,sans-serif",
  bodyFont: "'Hanken Grotesk','Segoe UI',system-ui,sans-serif",
  headSize: '7.2', headWeight: '800', headTrack: '-0.02em', subSize: '3.4', bodySize: '2.6',
  eyebrowSize: '1.6', statSize: '6.4', pad: '7',
};

/** percent-of-width → px on the fixed 1920 canvas */
const px = (pct, fallback) => (Number(pct ?? fallback) / 100) * W;

/**
 * AUTO-FIT. Patrick 2026-08-10: "the new slides W01, W02 — they're so large,
 * they don't even fit on the page."
 *
 * He is right, and the cause is that theme type sizes are fixed while slide
 * content is not. A four-word headline and a fourteen-word headline were being
 * drawn at the same 138px, so the long one ran off the canvas — silently, because
 * the slide has overflow:hidden and simply clips.
 *
 * 🔴 THE HEADLINE SHRINKS WITH ITS OWN LENGTH, and separately with how much else
 * is on the slide. Two independent pressures: a long headline needs smaller type
 * to fit its own lines, and a slide carrying six bullets needs the headline to
 * yield space to them. Multiplying both factors handles the case that actually
 * broke — a long headline AND a full body AND bullets, all at once.
 *
 * Floor of 0.55 is deliberate: below that the type drops under the organisers'
 * 24pt minimum, and a slide that unreadable should be EDITED, not shrunk. The
 * editor shows a warning instead.
 */
/** Bullets are [{text,on}] since CON-1610f; older rows may still be plain strings. */

/**
 * 🔴 WHAT THE CANVAS ACTUALLY RENDERS — raw first, `resolved` only for tokens.
 *
 * Patrick 2026-08-12: "if i edit headline, it doesnt automatically show live on top
 * unless i go to another slide and go back to it again."
 *
 * `resolved` is the server's token-expanded copy ({{stat:venues}} -> "1,420"). The
 * canvas used it exclusively, so anything that left `resolved` even momentarily
 * behind the raw field — a save round-trip adopting the server copy, a patch that
 * did not reach it — froze the preview until the slide was reselected and rebuilt.
 *
 * Rule: if the raw field carries NO {{ }} token, it is the truth and wins, so typing
 * is live. If it does carry a token, the resolved copy wins, so the expanded number
 * still renders. Tokens re-expand on the next save, which is the only moment they can
 * change anyway.
 */
const HAS_TOKEN = /\{\{/;
const pick = (raw, res) => (raw != null && !HAS_TOKEN.test(String(raw)) ? raw : (res ?? raw));
const resolveView = (slide) => {
  const r = slide?.resolved || {};
  const out = { ...r };
  ['eyebrow', 'headline', 'subhead', 'body', 'speaker_notes'].forEach((k) => {
    out[k] = pick(slide?.[k], r[k]);
  });
  // 🔴 A CARD'S HEADING CAN CARRY A TOKEN TOO. This picked only `text`, so
  // STATUS-01 — whose live numbers sit in the card HEAD, e.g.
  // "{{stat:venues.total}} Venues Mapped" — rendered the raw token on the slide.
  // Patrick 2026-08-14: "the deck doesnt show the numbers live, it shows
  // {{stat:venues.total}} V in the editor." Same raw-wins-unless-tokenised rule
  // applies per field, so typing in the head stays live.
  const rawB = Array.isArray(slide?.bullets) ? slide.bullets : null;
  const resB = Array.isArray(r.bullets) ? r.bullets : null;
  if (rawB) {
    out.bullets = rawB.map((b, i) => {
      const bo = typeof b === 'string' ? { text: b, on: true } : (b || {});
      const ro = resB && resB[i] ? (typeof resB[i] === 'string' ? { text: resB[i] } : resB[i]) : {};
      return {
        ...bo,
        head: pick(bo.head, ro.head),
        label: pick(bo.label, ro.label),
        text: pick(bo.text, ro.text),
        long: pick(bo.long, ro.long),
      };
    });
  } else if (resB) { out.bullets = resB; }
  return out;
};

/**
 * 🔴 THE FULL OBJECTS. activeBullets() below ends with `.map(b => b.text)` because
 * every list layout only ever needed the string — but that silently threw away
 * `head`, `label` and `say`, so the cards layout got text with no header to draw.
 * That one lost field made the header, its colour control and its size slider all
 * look broken at once. Anything that needs more than the text uses THIS.
 */
export function activeBulletObjects(slide) {
  const raw = resolveView(slide).bullets ?? [];
  return raw
    .map((b) => (typeof b === 'string' ? { text: b, on: true } : b))
    .filter((b) => b && b.on !== false && String(b.text || '').trim() !== '');
}

export function activeBullets(slide) {
  const raw = resolveView(slide).bullets ?? [];
  return raw
    .map((b) => (typeof b === 'string' ? { text: b, on: true } : b))
    .filter((b) => b && b.on !== false && String(b.text || '').trim() !== '')
    .map((b) => b.text);
}



export function fitFactors(slide) {
  const r = resolveView(slide);
  const headLen = String(r.headline || '').length;
  const bullets = activeBullets(slide);
  const bulletChars = bullets.reduce((a, b) => a + String(b).length, 0);
  const bodyLen = String(r.body || '').length + String(r.subhead || '').length;

  // headline: comfortable to ~46 chars, then taper
  let head = headLen <= 46 ? 1 : Math.max(0.5, 1 - (headLen - 46) * 0.0092);

  // crowding: everything else competing for the same canvas
  const load = bulletChars + bodyLen + bullets.length * 18;
  const crowd = load <= 200 ? 1 : Math.max(0.62, 1 - (load - 200) * 0.00055);

  head = Math.max(0.5, head * crowd);
  let bodyScale = Math.max(0.66, crowd);
  const auto = { head, body: bodyScale };

  // 🔴 A MANUAL SETTING BEATS THE AUTO-FIT, ALWAYS. Patrick asked for a control
  // to size the header himself and watch how much then fits. An auto-fit that
  // quietly overrode him would make that control feel broken.
  if (slide?.head_scale != null) head = Number(slide.head_scale);
  if (slide?.body_scale != null) bodyScale = Number(slide.body_scale);

  return { head, body: bodyScale, auto,
           manual: slide?.head_scale != null || slide?.body_scale != null,
           tight: head <= 0.56 || bodyScale <= 0.7 };
}


/**
 * CON-1640 — A DECK IS A LIST OF STEPS, NOT A LIST OF SLIDES.
 *
 * Patrick: "both in preview mode, not in PDF printing or rehearsal does it
 * respect the sub slides… at least when i rehearse now and click next and
 * record also it needs to respect the subslides, and on PDF as well."
 *
 * Sub-slides were only understood by the editor canvas and the live presenter.
 * The PDF export and the rehearsal recorder both iterate `slides` directly, so
 * a slide with four screenshots printed as ONE page and recorded as ONE
 * segment — the deck on paper and the deck being rehearsed were quietly
 * different from the deck being presented.
 *
 * expandSteps() is the single definition of what "next" means. Everything that
 * walks the deck uses it, so the presenter, the recorder, the player and the
 * PDF cannot drift apart again.
 *
 * Numbering follows how people already talk about them: slide 3, then 3a, 3b —
 * the base keeps the plain number so the deck's own numbering is unchanged by
 * adding a sub-slide.
 */
/**
 * CON-1650 — the sub-slides that are actually IN the sequence.
 *
 * Every surface that walks the deck must agree on this, or the presenter, the
 * PDF and the recording disagree about how many steps a slide has — which is the
 * exact class of bug that had sub-slides working in the editor and nowhere else.
 * One filter, exported, used by all of them. The EDITOR deliberately does not use
 * it: it lists disabled variants so they can be switched back on.
 */
export function activeVariants(slide) {
  const vs = Array.isArray(slide?.variants) ? slide.variants : [];
  return vs.filter((v) => v?.enabled !== false);
}

export function expandSteps(slides) {
  const out = [];
  (slides || []).forEach((s, i) => {
    const vs = activeVariants(s);
    const num = i + 1;
    /* CON-1649 — skip_base drops the slide's own image from the SEQUENCE when it
       is only there for the variants to inherit from. It still exists; it just
       is not a step you have to click past on stage. Ignored when there are no
       variants, or the slide would vanish entirely. */
    const skipBase = !!s?.skip_base && vs.length > 0;
    if (!skipBase) {
      out.push({ slide: s, slideIdx: i, variantIndex: 0, label: String(num), key: `${s.id}:0` });
    }
    vs.forEach((v, j) => {
      // With the base skipped the first variant IS the slide, so it takes the
      // plain number and lettering starts at the second.
      const label = skipBase
        ? (j === 0 ? String(num) : `${num}${String.fromCharCode(96 + j)}`)
        : `${num}${String.fromCharCode(97 + j)}`;
      out.push({
        slide: s, slideIdx: i, variantIndex: j + 1,
        label, variant: v, key: `${s.id}:${j + 1}`,
      });
    });
  });
  return out;
}

/** How many STEPS a slide contributes: base + variants, or just variants. */
export function stepsForSlide(slide) {
  const vs = activeVariants(slide);
  if (!vs.length) return 1;
  return slide?.skip_base ? vs.length : vs.length + 1;
}

/** The variantIndex the FIRST step of a slide should render. */
export function firstStepOf(slide) {
  const vs = activeVariants(slide);
  return (slide?.skip_base && vs.length) ? 1 : 0;
}

export default function SlideCanvas({
  slide,
  theme,            // { tokens }
  deck,             // for QR inheritance
  width = 960,
  showQr = true,
  // 🔴 THE OVERFLOW WARNING IS AN EDITOR TOOL. It used to piggyback on `showQr`,
  // which defaults to TRUE — so the giant red "↓ Npx below the fold" banner
  // rendered on the LIVE audience screen, the shared link, rehearsal playback and
  // inside the exported PDF. Patrick 2026-08-14: "the 77px below the field should
  // be invisible especially in the viewer." `showQr` answers "draw the QR code",
  // not "is a human editing this" — deriving one from the other is what shipped a
  // debugging overlay to an audience. Opt IN, and only the editor canvas does.
  showOverflow = false,
  onQrMove = null,      // (x,y) in percent — enables dragging in the editor
  onLogoMove = null,    // same contract for the logo
  /* CON-1632 — image studio.
     onImageMove enables dragging the PICTURE inside its frame (the frame stays
     put, the image pans), which is what "crop it" actually means here.
     variantIndex selects a sub-slide: same slide, different picture. */
  onImageMove = null,   // (x,y) in percent
  onTextMove = null,    // CON-1633 — drag the v8split text block (eyebrow + headline)
  variantIndex = 0,
  className = '',
  style = {},
}) {
  // the deck's chosen heading font overrides the theme token
  const t = useMemo(() => {
    const base = { ...DEFAULT_TOKENS, ...(theme?.tokens || {}) };
    if (deck?.head_font) base.headFont = `'${deck.head_font}','Hanken Grotesk',system-ui,sans-serif`;
    return base;
  }, [theme, deck?.head_font]);
  const scale = width / W;
  const r = resolveView(slide);
  const bul = activeBullets(slide);

  /**
   * 🔴 OVERFLOW IS MEASURED AND SHOWN, NOT CLIPPED.
   *
   * Patrick 2026-08-11: "I still don't understand what you're doing with the
   * overflow. Where's the overflow going right now? I don't see anything."
   *
   * Nowhere — that was the problem. The canvas is overflow:hidden, so anything
   * past 1080px was silently cut and there was nothing on screen to notice. Now
   * the content block is measured against the canvas height and, when it spills,
   * a red line is drawn at exactly the point where the projector stops showing
   * it, labelled with how much is below. You can see what you are losing.
   */
  const contentRef = React.useRef(null);
  const [overflowPx, setOverflowPx] = React.useState(0);
  // The measuring effect lives BELOW the padding computation — it needs the real
  // per-side values, and referencing them here would hit the temporal dead zone.

  // A slide may override the deck's QR target; `qr_enabled === null` means inherit.
  const qrOn = showQr
    && (slide?.qr_enabled ?? deck?.qr_enabled ?? true)
    && (slide?.qr_url || deck?.qr_url);
  const qrUrl = slide?.qr_url || deck?.qr_url || '';
  const qrCap = slide?.qr_caption || deck?.qr_caption || '';

  const accent = slide?.accent || t.accent;
  // 🔴 PER-SLIDE MARGIN. Patrick 2026-08-12: "the top margin is quite a lot, it
  // feels more than the sides… can i edit the margins somewhere?" The theme pad is
  // uniform, so top and sides ARE equal in pixels — what made the top look bigger
  // was the content column being vertically centred (see vJustify below). This
  // multiplier is still worth having: a crowded slide can tighten, a sparse one
  // can breathe, without touching the theme every other slide shares.
  const padMul = Number(slide?.pad_scale) > 0 ? Number(slide.pad_scale) : 1;
  const padBase = px(t.pad, 7) * padMul;
  // 🔴 PER-SIDE. Scaling all four together was useless — the complaint is always
  // the TOP specifically. Each side is a % of slide WIDTH (so top and left read as
  // the same visual distance on a 16:9 canvas); NULL falls back to the theme pad.
  const sidePad = (v) => (v == null || v === '' ? padBase : px(Number(v), 7));
  const padT = sidePad(slide?.pad_top);
  const padR = sidePad(slide?.pad_right);
  const padB = sidePad(slide?.pad_bottom);
  const padL = sidePad(slide?.pad_left);
  const pad = padBase;   // legacy single value, still used by the image layout

  // 🔴 MEASURE AGAINST THE PADDING THIS SLIDE ACTUALLY USES.
  // The old version computed `usable = H - themePad * 2`, ignoring both the
  // pad_scale multiplier and every per-side override — so on any slide with a
  // custom top or bottom margin (by now, most of them) the number was simply
  // wrong, and often claimed overflow on a slide that fits. Patrick 2026-08-14:
  // "it's buggy even in the editor, it doesn't say the truth always."
  // Skipped entirely when the warning is not displayed: no ResizeObserver, no
  // measurement, no state churn on an audience screen.
  React.useLayoutEffect(() => {
    if (!showOverflow) { setOverflowPx(0); return; }
    const el = contentRef.current;
    if (!el) return;
    const measure = () => {
      const inner = el.scrollHeight;
      const usable = H - (['image','v8split'].includes(slide?.layout) ? 0 : padT + padB);
      // 1px of rounding is not overflow — sub-pixel layout maths would otherwise
      // flash the red line on a slide that fits exactly.
      const over = Math.round(inner - usable);
      setOverflowPx(over > 1 ? over : 0);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [slide, padT, padB, width, showOverflow]);

  // NULL keeps the historic centred behaviour, so no existing slide moves.
  // 🔴 SETTING A TOP MARGIN IMPLIES TOP ALIGNMENT. With the column centred, the
  // margin is only a MINIMUM — so typing 0 changed nothing visible and read as
  // broken. If a top margin is given explicitly, start the content there unless
  // centring was explicitly chosen.
  // 🔴 AN EXPLICIT TOP MARGIN WINS. Slide W01 had v_align='center' AND pad_top=0,
  // and centring won — so typing 0 changed nothing and read as broken twice. If you
  // have said where the top edge is, that is the instruction; the align buttons only
  // decide slides that have NOT been given a top margin.
  const vJustify = slide?.pad_top != null
    ? 'flex-start'
    : (slide?.v_align === 'top' ? 'flex-start' : 'center');
  /* ==========================================================================
   * CON-1632 — THE IMAGE LAYER
   *
   * One place that answers "which picture, and how is it framed", used by every
   * layout that shows one. Before this, `split`, `image` and `v8split` each
   * inlined their own <img> with objectFit and nothing else, so a picture could
   * only be stretched two ways and any real cropping meant editing the file and
   * re-uploading it.
   *
   * A VARIANT is the same slide with a different picture (see
   * pitch.slide_variants). Its transform fields fall back to the slide's, so a
   * set of screenshots that share a crop is configured once and each variant
   * only carries what differs.
   * ======================================================================== */
  const variants = activeVariants(slide);   // CON-1650 — disabled sub-slides are not steps
  /**
   * 🔴 CON-1639 — STEP 0 IS THE SLIDE'S OWN PICTURE, NOT THE FIRST SUB-SLIDE.
   *
   * variantIndex is a STEP, not an array index: 0 means the base image and
   * 1..n select variants[0..n-1]. Both callers already worked that way — the
   * editor's picker (Base image, then one button per sub-slide) and the
   * presenter's go(), which walks 0..n and shows a dot per step. Only this line
   * treated the number as a plain array index, so step 0 rendered variants[0].
   *
   * The result was invisible until a variant had its own picture: with the base
   * image showing through as the fallback, every step looked identical. Once
   * Patrick uploaded one, the first sub-slide's image appeared on the base and
   * every step was off by one. Patrick: "it shows the image i uploaded for first
   * sub slide Lee on the base image."
   */
  const vStep = Math.max(0, Math.min(variants.length, Number(variantIndex) || 0));
  const activeVariant = vStep > 0 ? variants[vStep - 1] : null;

  const img = (activeVariant && (activeVariant.image_url || activeVariant.media_url))
    || slide?.image_url || slide?.media_url;

  // Variant value wins; NULL/'' means inherit the slide. `?? ` alone is wrong
  // here because an empty string from a cleared input must also inherit.
  const iv = (f, dflt) => {
    const a = activeVariant ? activeVariant[f] : null;
    if (a !== null && a !== undefined && a !== '') return Number(a);
    const b = slide?.[f];
    if (b !== null && b !== undefined && b !== '') return Number(b);
    return dflt;
  };
  const ivs = (f, dflt) => {
    const a = activeVariant ? activeVariant[f] : null;
    if (a) return a;
    return slide?.[f] || dflt;
  };

  const imgScale = iv('img_scale', 1);
  const imgX = iv('img_x', 50);
  const imgY = iv('img_y', 50);
  const imgRot = iv('img_rot', 0);
  const imgOpacity = iv('img_opacity', 1);
  const imgRadius = iv('img_radius', 0);
  const imgBlend = ivs('img_blend', 'normal');
  const imgFrame = ivs('img_frame', 'none');
  const imgGrad = slide?.img_grad || '';
  const imgShadow = slide?.img_shadow === true;

  /**
   * SlideImage — the picture, transformed.
   *
   * 🔴 THE FRAME NEVER MOVES; THE PICTURE MOVES INSIDE IT. objectPosition pans
   * and scale zooms, both inside a fixed overflow:hidden box, so a crop changes
   * nothing about the slide's composition — which is the whole reason a set of
   * screenshots can share one layout and still each be framed correctly.
   *
   * Dragging writes img_x/img_y, so the crop is stored as data and survives a
   * re-upload of the same screenshot at a different size.
   */
  const SlideImage = ({ radius = null, fitDefault = 'cover', style: extra = {} }) => {
    if (!img) return null;
    /* 'fit-exposed' is a choice about the BOX, not a CSS object-fit value —
       it implies contain inside that box. Mapping it here keeps the rest of the
       component from having to know the difference. */
    const rawFit = slide?.image_fit || fitDefault;
    const fit = rawFit === 'fit-exposed' ? 'contain' : rawFit;
    const dragging = React.useRef(null);
    const phone = imgFrame === 'phone', browser = imgFrame === 'browser';
    const inner = (
      <img
        src={img}
        alt={activeVariant?.caption || slide?.media_alt || ''}
        draggable={false}
        onPointerDown={onImageMove ? (e) => {
          /* Pan the picture. The delta is scaled by how far the image can
             actually travel — at scale 1 with objectFit:cover there is only the
             overflow to move through, so a raw pixel delta would feel wildly
             fast on one slide and dead on another. */
          e.preventDefault(); e.stopPropagation();
          const host = e.currentTarget.parentElement.getBoundingClientRect();
          const start = { mx: e.clientX, my: e.clientY, x: imgX, y: imgY };
          dragging.current = start;
          const move = (ev) => {
            const dx = ((ev.clientX - start.mx) / host.width) * 100;
            const dy = ((ev.clientY - start.my) / host.height) * 100;
            /* The picture now follows the pointer literally - drag right and it
               goes right. Under objectPosition it had to be inverted (a higher
               position % reveals more of the RIGHT, so the image slid left):
               correct for a crop window, wrong for something you are holding. */
            const nx = Math.max(0, Math.min(100, start.x + dx / 2));
            const ny = Math.max(0, Math.min(100, start.y + dy / 2));
            onImageMove(Math.round(nx * 10) / 10, Math.round(ny * 10) / 10);
          };
          const up = () => {
            dragging.current = null;
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', up);
          };
          window.addEventListener('pointermove', move);
          window.addEventListener('pointerup', up);
        } : undefined}
        style={{
          width: '100%', height: '100%', display: 'block',
          objectFit: fit,
          /* CON-1637 - PAN WITH transform, NOT objectPosition.
           *
           * Patrick: "panning it to the right outside of the screen wouldn't work
           * either." Correct, and objectPosition can never do it: it only chooses
           * WHICH PART OF THE OVERFLOW to show. With contain, or at any zoom of 1x
           * or less, there IS no overflow - so the control did nothing at all,
           * which is exactly the case a portrait photo lands in.
           *
           * translate moves the element itself, so the picture can be pushed
           * anywhere including past the frame edge (clipped by the wrapper's
           * overflow:hidden). img_x/img_y stay 0-100 with 50 = centred and map to
           * -100%..+100% of the element - one full frame of travel each way,
           * enough to move a photo completely out of shot.
           *
           * objectPosition is pinned to center so the two systems cannot fight:
           * one thing moves the picture, and it is this. */
          objectPosition: 'center',
          transform: `translate(${(imgX - 50) * 2}%, ${(imgY - 50) * 2}%) scale(${imgScale})${imgRot ? ` rotate(${imgRot}deg)` : ''}`,
          transformOrigin: 'center',
          opacity: imgOpacity,
          mixBlendMode: imgBlend === 'normal' ? undefined : imgBlend,
          cursor: onImageMove ? 'grab' : 'default',
          userSelect: 'none',
        }}
      />
    );
    const r = radius != null ? radius : imgRadius;
    return (
      <div style={{
        position: 'relative', width: '100%', height: '100%', overflow: 'hidden',
        borderRadius: phone ? px(3.4, 3.4) : (browser ? px(1.2, 1.2) : r),
        border: phone ? `${px(0.55,0.55)}px solid #12161f` : (browser ? '1px solid rgba(255,255,255,.14)' : 'none'),
        boxShadow: imgShadow ? '0 30px 80px rgba(0,0,0,.45)' : 'none',
        background: t.bgAlt,
        ...extra,
      }}>
        {browser && (
          /* A browser chrome bar makes a screenshot read as a product shot
             rather than a floating rectangle. Purely decorative. */
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, height: px(1.6, 1.6),
            background: '#1b202b', display: 'flex', alignItems: 'center',
            gap: px(0.4, 0.4), paddingLeft: px(0.8, 0.8), zIndex: 2,
          }}>
            {['#ff5f57', '#febc2e', '#28c840'].map(c => (
              <span key={c} style={{ width: px(0.42,0.42), height: px(0.42,0.42), borderRadius: '50%', background: c }} />
            ))}
          </div>
        )}
        <div style={{ position: 'absolute', inset: 0, top: browser ? px(1.6,1.6) : 0 }}>{inner}</div>
        {imgGrad ? (
          <div style={{ position: 'absolute', inset: 0, background: imgGrad, pointerEvents: 'none' }} />
        ) : null}
      </div>
    );
  };

  const fit = useMemo(() => fitFactors(slide), [slide]);

  /* 🔴 CON-1633 — A TYPED LINE BREAK MUST RENDER AS A LINE BREAK.
   *
   * Patrick: "the header still doesnt support line break, if i have a line break
   * in the field down i want it to respect that."
   *
   * These are <div>/<h1> elements, and HTML collapses newlines to a space — so
   * pressing Enter in the headline field did nothing visible and the only way to
   * control where a long headline broke was to fight the column width. Every
   * text style now uses `pre-line`: typed newlines break, runs of spaces still
   * collapse (so pasted copy does not arrive full of gaps), and a line with no
   * newline in it wraps normally as before.
   */
  const preLine = { whiteSpace: 'pre-line' };
  const headStyle = {
    fontFamily: t.headFont, fontWeight: Number(t.headWeight) || 800,
    fontSize: px(t.headSize, 7.2) * fit.head, lineHeight: 1.06, letterSpacing: t.headTrack,
    color: t.ink, margin: 0, ...preLine,
  };
  const subStyle = {
    fontFamily: t.bodyFont, fontWeight: 500, fontSize: px(t.subSize, 3.4) * fit.body,
    // 🔴 was hardcoded to `accent` — which is why every subhead was orange and
    // nothing could change it. Falls back to accent so no existing slide moves.
    lineHeight: 1.22, color: t.subInk || accent, margin: 0, ...preLine,
  };
  const bodyStyle = {
    fontFamily: t.bodyFont, fontWeight: 400, fontSize: px(t.bodySize, 2.6) * fit.body,
    lineHeight: 1.38, color: t.inkSoft, margin: 0, ...preLine,
  };
  const eyebrowStyle = {
    fontFamily: t.bodyFont, fontWeight: 700, fontSize: px(t.eyebrowSize, 1.6),
    letterSpacing: '0.18em', textTransform: 'uppercase', color: t.inkFaint, margin: 0,
  };

  const Eyebrow = () => (r.eyebrow ? <div style={eyebrowStyle}>{r.eyebrow}</div> : null);

  // Two-column layout when the slide asks for it. Patrick: "maybe even have two
  // column bullet points, three on the left, three on the right." Columns buy
  // roughly 40% more content at the same type size, which is the real fix for a
  // dense problem slide — shrinking the font is not.
  const cols = Number(slide?.bullet_columns) === 2 ? 2 : 1;
  const Bullets = ({ items, gap = 26 }) => (
    <ul style={{ listStyle: 'none', padding: 0, margin: 0,
                 display: cols === 2 ? 'grid' : 'flex', flexDirection: 'column',
                 gridTemplateColumns: cols === 2 ? '1fr 1fr' : undefined,
                 columnGap: cols === 2 ? px(3.2, 3.2) : 0, rowGap: gap, gap: cols === 2 ? undefined : gap }}>
      {(items || []).filter(Boolean).map((b, i) => (
        <li key={i} style={{ ...bodyStyle, display: 'flex', gap: 24, alignItems: 'flex-start' }}>
          <span style={{
            flex: '0 0 auto', width: px(0.7, 0.7), height: px(0.7, 0.7), borderRadius: 99,
            background: accent, marginTop: px(t.bodySize, 2.6) * 0.52,
          }} />
          <span>{b}</span>
        </li>
      ))}
    </ul>
  );

  const Qr = ({ size = 190 }) => {
    if (!qrOn) return null;
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 22 }}>
        <div style={{ background: t.qrBg, padding: 14, borderRadius: 16, lineHeight: 0 }}>
          <QRCodeSVG value={qrUrl} size={size} level="M" bgColor={t.qrBg} fgColor={t.qrInk} />
        </div>
        {qrCap ? (
          <div style={{
            fontFamily: t.bodyFont, fontSize: px(1.5, 1.5), lineHeight: 1.3,
            color: t.inkFaint, maxWidth: 420, fontWeight: 600,
          }}>{qrCap}</div>
        ) : null}
      </div>
    );
  };

  const Stats = () => {
    const items = slide?.stats || [];
    if (!items.length) return null;
    const cols = items.length <= 3 ? items.length : 3;
    return (
      <div style={{
        display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gap: `${px(3, 3)}px ${px(3.5, 3.5)}px`, width: '100%',
      }}>
        {items.map((s) => (
          <div key={s.key} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{
              fontFamily: t.headFont, fontWeight: 800, fontSize: px(t.statSize, 6.4),
              lineHeight: 1, color: accent, letterSpacing: '-0.02em',
            }}>{s.value}</div>
            <div style={{
              fontFamily: t.bodyFont, fontSize: px(1.7, 1.7), lineHeight: 1.25,
              color: t.inkFaint, fontWeight: 600,
            }}>{s.label || s.key}</div>
          </div>
        ))}
      </div>
    );
  };

  // ── layouts ──────────────────────────────────────────────────────
  const layout = slide?.layout || 'statement';
  let content;

  if (layout === 'title') {
    content = (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: vJustify, gap: px(2.4, 2.4) }}>
        <Eyebrow />
        <h1 style={{ ...headStyle, fontSize: px(Number(t.headSize) * 1.12, 8) * fit.head }}>{r.headline}</h1>
        {r.subhead ? <p style={subStyle}>{r.subhead}</p> : null}
        {bul.length ? <div style={{ marginTop: px(1.4, 1.4) }}><Bullets items={bul} /></div> : null}
        <div style={{ marginTop: 'auto' }}>
          <div style={{ height: 6, width: px(9, 9), background: accent, borderRadius: 4 }} />
        </div>
      </div>
    );
  } else if (layout === 'closer') {
    content = (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', alignItems: 'center', justifyContent: 'center', textAlign: 'center', gap: px(2.2, 2.2) }}>
        <h1 style={{ ...headStyle, fontSize: px(Number(t.headSize) * 1.05, 7.6) * fit.head }}>{r.headline}</h1>
        {r.subhead ? <p style={{ ...subStyle, color: t.inkSoft }}>{r.subhead}</p> : null}
        {qrOn ? (
          <div style={{ marginTop: px(1.6, 1.6), display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
            <div style={{ background: t.qrBg, padding: 20, borderRadius: 22, lineHeight: 0 }}>
              <QRCodeSVG value={qrUrl} size={310} level="M" bgColor={t.qrBg} fgColor={t.qrInk} />
            </div>
            {qrCap ? <div style={{ ...bodyStyle, fontSize: px(1.7, 1.7), color: t.inkFaint, fontWeight: 600 }}>{qrCap}</div> : null}
          </div>
        ) : null}
      </div>
    );
  } else if (layout === 'stats') {
    content = (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: px(2.2, 2.2) }}>
        <Eyebrow />
        <h1 style={{ ...headStyle, fontSize: px(Number(t.headSize) * 0.82, 5.9) * fit.head }}>{r.headline}</h1>
        {r.subhead ? <p style={{ ...subStyle, fontSize: px(Number(t.subSize) * 0.85, 2.9) * fit.body }}>{r.subhead}</p> : null}
        <div style={{ marginTop: px(1, 1), flex: 1, display: 'flex', alignItems: 'center' }}><Stats /></div>
        {r.body ? <p style={{ ...bodyStyle, fontSize: px(1.7, 1.7), color: t.inkFaint, maxWidth: '62%' }}>{r.body}</p> : null}
      </div>
    );
  } else if (layout === 'split') {
    content = (
      <div style={{ display: 'flex', height: '100%', gap: px(4, 4) }}>
        <div style={{ flex: '1 1 46%', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: px(1.8, 1.8) }}>
          <Eyebrow />
          <h1 style={{ ...headStyle, fontSize: px(Number(t.headSize) * 0.86, 6.2) * fit.head }}>{r.headline}</h1>
          {r.subhead ? <p style={{ ...subStyle, fontSize: px(Number(t.subSize) * 0.86, 2.9) * fit.body }}>{r.subhead}</p> : null}

        </div>
        <div style={{ flex: '1 1 54%', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: px(1.6, 1.6) }}>
          {img ? (
            /* CON-1632 — was a bare <img> with objectFit and nothing else, so the
               right-hand picture on every split slide could not be panned, zoomed,
               rounded or blended. Now the same image layer every other layout uses. */
            <div style={{ flex: 1, minHeight: 0 }}>
              <SlideImage radius={imgRadius || 24} />
            </div>
          ) : null}
          {bul.length ? <Bullets items={bul} /> : null}
          {r.body ? <p style={bodyStyle}>{r.body}</p> : null}
        </div>
      </div>
    );
  } else if (layout === 'image') {
    // v70.325 — overlay_enabled === false renders the picture BARE.
    //
    // Patrick 2026-08-15 on V8: "the repeated header is too fat and cant be
    // position. can we have a blend me out button for that please"
    //
    // The scrim + headline are right for a photo that needs a caption, and wrong
    // for finished full-bleed artwork that already carries its own title — there
    // the title was drawn twice and the gradient dimmed a completed design.
    // Defaults to true, so every pre-existing image slide is untouched.
    const bare = slide?.overlay_enabled === false;
    content = (
      <div style={{ position: 'absolute', inset: 0 }}>
        {img ? <SlideImage radius={0} /> : null}
        {!bare && (
          <>
            <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(180deg, transparent 30%, ${t.bg}f2 100%)` }} />
            <div style={{ position: 'absolute', left: pad, right: pad, bottom: pad, display: 'flex', flexDirection: 'column', gap: px(1.4, 1.4) }}>
              <Eyebrow />
              <h1 style={{ ...headStyle, fontSize: px(Number(t.headSize) * 0.9, 6.5) * fit.head }}>{r.headline}</h1>
              {r.subhead ? <p style={subStyle}>{r.subhead}</p> : null}
            </div>
          </>
        )}
      </div>
    );
  } else if (layout === 'quote') {
    content = (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'center', gap: px(2.4, 2.4) }}>
        <div style={{ fontFamily: t.headFont, fontSize: px(9, 9), lineHeight: 0.7, color: accent, fontWeight: 800 }}>&ldquo;</div>
        <h1 style={{ ...headStyle, fontSize: px(Number(t.headSize) * 0.8, 5.8) * fit.head, fontWeight: 600, lineHeight: 1.16 }}>{r.headline}</h1>
        {r.subhead ? <p style={{ ...bodyStyle, fontSize: px(2.1, 2.1), color: t.inkFaint }}>{r.subhead}</p> : null}
      </div>
    );
  } else if (layout === 'bullets') {
    content = (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: px(2, 2) }}>
        <Eyebrow />
        <h1 style={{ ...headStyle, fontSize: px(Number(t.headSize) * 0.84, 6.0) * fit.head }}>{r.headline}</h1>
        {r.subhead ? <p style={{ ...subStyle, fontSize: px(Number(t.subSize) * 0.85, 2.9) * fit.body }}>{r.subhead}</p> : null}
        <div style={{ marginTop: px(1.2, 1.2), flex: 1 }}><Bullets items={bul} gap={px(1.7, 1.7)} /></div>
        {r.body ? <p style={{ ...bodyStyle, fontSize: px(1.9, 1.9), color: t.ink, fontWeight: 600, maxWidth: '64%' }}>{r.body}</p> : null}
      </div>
    );
  } else if (layout === 'v8split') {
    /**
     * 🔴 V8SPLIT — THE V8 SLIDE, REBUILT FROM THE POWERPOINT'S OWN GEOMETRY.
     *
     * Patrick 2026-08-17: "I wanted the exact looks of v eight, the exact layout,
     * and everything with the text of what I sent you from ChatGPT… The new split
     * layout version that you did doesn't render the same. It looks shit."
     *
     * He was right twice. First I pointed V9 at V8's exported PNGs, which have the
     * OLD wording baked into the pixels, so editing a card changed nothing. Then I
     * rebuilt the composition BY EYE from a screenshot, which is how you get
     * something that is roughly right and therefore wrong.
     *
     * Every number below is READ OUT OF HonestLee_9_Slide_Deck.pptx — the shape
     * offsets, extents, colours and point sizes in ppt/slides/slideN.xml — not
     * estimated. The deck is 12192000 x 6858000 EMU (13.333in x 7.5in), so on this
     * fixed 1920x1080 canvas 1pt = 2px exactly, and every position is a percentage
     * of the slide, which is resolution-independent by construction.
     *
     * Stacking order, exactly as the PowerPoint has it:
     *   1. photo, full bleed 100% x 100%
     *   2. #0D1629 panel at 96% alpha over the left 60.5%
     *   3. #0D1629 strip at 52% alpha from 59.4%, 11.7% wide — the fade that stops
     *      the panel edge cutting the photo in a hard line
     *   4. text
     * The photo is NEVER a background the text sits on; it is its own layer, which
     * is also the import package's IMAGE-ASSET RULE.
     */
    // 🔴 NUDGEABLE. Patrick 2026-08-17: "i cant drag them around". Absolute
    // PowerPoint coordinates gave an exact match and took away every control at the
    // same time. The per-slide margin sliders he already has now act as OFFSETS
    // from the extracted baseline: pad_left/pad_top shift the whole text block,
    // pad_right pulls the card columns in. Nothing new to learn, no migration, and
    // leaving them all at 0 renders exactly as the PowerPoint does.
    const nudgeX = (Number(slide?.pad_left) || 0) - (Number(slide?.pad_right) || 0);
    const nudgeY = (Number(slide?.pad_top) || 0) - (Number(slide?.pad_bottom) || 0);
    const P = (n) => `${n}%`;
    const PX = (n) => `calc(${n}% + ${nudgeX}px)`;   // horizontal, nudged
    const PY = (n) => `calc(${n}% + ${nudgeY}px)`;   // vertical, nudged
    const pt = (n) => n * 2;                       // 1pt = 2px on the 1920 canvas
    const cards6 = (r.bullets || []).filter(b => b && b.on !== false).slice(0, 6);
    const COL_X = [4.7, 30.5];                     // two card columns
    // 🔴 +1.2% on every row. Patrick 2026-08-17: "the headline and the text is too
    // close to each other." The PowerPoint's 35.8% start was tuned for ITS headline;
    // the headline-led cut runs longer and closes the gap. This buys back the air
    // without disturbing the 16.4% row pitch, and row 3 still clears the closing
    // line (73.9+1.2+10.6 = 85.7% vs the closing at 90.3%).
    const ROW_Y = [35.8, 52.2, 68.6].map(v => v + 1.2);
    // 🔴 TWO BRAND COLOURS, not two near-identical whites. The extracted deck had
    // heads #F7F8FC and body #D7DEEC — a 6% difference nobody reads as a hierarchy.
    // Heads now carry HonestLee orange (the same accent as the numbers) and the body
    // drops to a muted blue-grey, so the eye lands on the heading first.
    const INK = { eyebrow: '#91A0BC', head: '#F4772C', rule: '#2658A0',
                  num: '#F4772C', cardHead: '#E46E34', cardText: '#A9B6CE' };

    /* ======================================================================
     * CON-1633 — the panel, the fade and the photo side are now DATA.
     *
     * Every value here was a literal, which is why the image studio's blend and
     * background controls appeared to do nothing on this layout and why the fade
     * was permanently on. Defaults reproduce the extracted PowerPoint exactly,
     * so an untouched slide is byte-identical to before.
     * ==================================================================== */
    const pnlColor = slide?.panel_color || '#0D1629';
    const pnlW = slide?.panel_width != null && slide.panel_width !== '' ? Number(slide.panel_width) : 60.5;
    const pnlOp = slide?.panel_opacity != null && slide.panel_opacity !== ''
      ? Number(slide.panel_opacity) : (img ? 0.96 : 1);
    const fadeOn = slide?.fade_enabled !== false;          // NULL = on, as it always was
    const fadeW = slide?.fade_width != null && slide.fade_width !== '' ? Number(slide.fade_width) : 11.7;
    const fadeOp = slide?.fade_opacity != null && slide.fade_opacity !== '' ? Number(slide.fade_opacity) : 0.52;
    // Photo on the right is the PowerPoint. Flipping mirrors the whole composition.
    const imgLeft = slide?.image_side === 'left';
    // rgba of the panel colour, so the fade dissolves into the PANEL rather than
    // into a hardcoded navy that no longer matches once the panel is recoloured.
    const toRgba = (hex, a) => {
      const m = /^#?([0-9a-f]{6})$/i.exec(String(hex).trim());
      if (!m) return hex;
      const n = parseInt(m[1], 16);
      return `rgba(${(n >> 16) & 255},${(n >> 8) & 255},${n & 255},${a})`;
    };
      /* Dragging the text block writes text_x/text_y as a % OFFSET from the
       PowerPoint baseline rather than absolute coordinates, so "reset" is
       literally 0,0 and the extracted composition is always recoverable. */
    const startTextDrag = (e) => {
      if (!onTextMove) return;
      e.preventDefault(); e.stopPropagation();
      const host = e.currentTarget.closest('[data-slide-root]') || e.currentTarget.parentElement;
      const box = host.getBoundingClientRect();
      const start = { mx: e.clientX, my: e.clientY, x: Number(slide?.text_x) || 0, y: Number(slide?.text_y) || 0 };
      const move = (ev) => {
        const dx = ((ev.clientX - start.mx) / box.width) * 100;
        const dy = ((ev.clientY - start.my) / box.height) * 100;
        onTextMove(Math.round((start.x + dx) * 10) / 10, Math.round((start.y + dy) * 10) / 10);
      };
      const up = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
    };
  const textNudgeX = Number(slide?.text_x) || 0;
    const textNudgeY = Number(slide?.text_y) || 0;
    const headW = slide?.head_width != null && slide.head_width !== '' ? Number(slide.head_width) : 64.1;

    content = (
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden' }}>
        {img ? (() => {
          /* 🔴 CON-1636 — "SHOW WHOLE IMAGE" HAS TO MEAN THE VISIBLE AREA.
           *
           * The photo is layer 1, full bleed, with the panel painted over the
           * left 60.5%. So a `contain` image is centred in the WHOLE slide and
           * two thirds of it end up behind the panel — the setting says "show
           * everything" and the picture still looks cut off, which is exactly
           * what Patrick hit with a portrait shot.
           *
           * When the user has explicitly asked to see the whole picture, the
           * image box is therefore constrained to the EXPOSED strip. `cover`
           * stays full-bleed: there the photo IS the background and bleeding
           * under the panel is the point.
           */
          /* 🔴 CON-1638 — PANEL WIDTH MOVES THE PANEL. IT DOES NOT RESIZE THE PHOTO.
           *
           * v70.382 constrained the image box to the exposed strip whenever the
           * fit was `contain`, which meant dragging the panel-width slider
           * changed the photo's container and the picture appeared to zoom
           * instead of the fade line sliding across it. Patrick: "you broke the
           * panel width setting which worked before, it's now simply zooming the
           * photo instead of making the fade transition line left to right."
           *
           * Two controls silently doing each other's job. The photo is full
           * bleed again — the original behaviour — and fitting it to the visible
           * area is now an EXPLICIT fit mode you choose, not a side effect of
           * choosing `contain`. */
          const fitExposed = slide?.image_fit === 'fit-exposed';
          const exposed = Math.max(12, 100 - pnlW);
          const box = fitExposed
            ? (imgLeft
                ? { left: 0, width: P(exposed), top: 0, height: '100%' }
                : { right: 0, width: P(exposed), top: 0, height: '100%' })
            : { inset: 0 };
          return (
            <div style={{ position: 'absolute', ...box,
                          transform: imgLeft && !fitExposed ? 'scaleX(-1)' : undefined }}>
              {/* Mirrored container + counter-mirrored image: flips WHICH SIDE the
                  photo is exposed on without mirroring the photograph itself, which
                  would put text in a screenshot back to front. */}
              <div style={{ position: 'absolute', inset: 0,
                            transform: imgLeft && !fitExposed ? 'scaleX(-1)' : undefined }}>
                <SlideImage radius={0} />
              </div>
            </div>
          );
        })() : null}

        {/* the panel */}
        <div style={{
          position: 'absolute', top: 0, height: '100%',
          left: imgLeft ? undefined : 0, right: imgLeft ? 0 : undefined,
          width: P(pnlW), background: pnlColor, opacity: pnlOp,
        }} />

        {/* the fade that dissolves the panel edge into the photo */}
        {img && fadeOn ? (
          <div style={{
            position: 'absolute', top: 0, height: '100%', width: P(fadeW),
            left: imgLeft ? undefined : P(pnlW - 1.1),
            right: imgLeft ? P(pnlW - 1.1) : undefined,
            background: `linear-gradient(${imgLeft ? 270 : 90}deg, ${toRgba(pnlColor, 1)} 0%, ${toRgba(pnlColor, 0)} 100%)`,
            opacity: fadeOp,
          }} />
        ) : null}

        {r.eyebrow ? (
          <div onPointerDown={onTextMove ? startTextDrag : undefined}
            style={{ position: 'absolute', left: PX(4.7 + textNudgeX), top: PY(5.3 + textNudgeY), width: P(50.8),
                     cursor: onTextMove ? 'grab' : undefined,
                        color: INK.eyebrow, fontFamily: t.headFont, fontWeight: 700,
                        fontSize: pt(12), letterSpacing: '0.06em',
                        whiteSpace: 'pre-line' }}>{r.eyebrow}</div>
        ) : null}

        {r.headline ? (
          <div onPointerDown={onTextMove ? startTextDrag : undefined}
            style={{ position: 'absolute', left: PX(4.5 + textNudgeX), top: PY(12.2 + textNudgeY), width: P(headW),
                     cursor: onTextMove ? 'grab' : undefined,
                        color: INK.head, fontFamily: t.headFont, fontWeight: 800,
                        fontSize: pt(27) * fit.head, lineHeight: 1.18, letterSpacing: '-0.01em',
                        whiteSpace: 'pre-line' }}>
            {r.headline}
          </div>
        ) : null}

        {cards6.map((bo, i) => {
          const cx = COL_X[i % 2];
          const ry = ROW_Y[Math.floor(i / 2)];
          const headScale = Number(slide?.card_head_scale) > 0 ? Number(slide.card_head_scale) : 1;
          const bodyScale = Number(slide?.card_body_scale) > 0 ? Number(slide.card_body_scale) : 1;
          return (
            <React.Fragment key={i}>
              {/* the thin blue rule that caps each card */}
              <div style={{ position: 'absolute', left: PX(cx), top: PY(ry), width: P(22.3),
                            height: 3, background: slide?.card_line || INK.rule }} />
              <div style={{ position: 'absolute', left: PX(cx), top: PY(ry + 1.4), width: P(2.8),
                            color: INK.num, fontFamily: t.headFont, fontWeight: 800, fontSize: pt(11.25) }}>
                {String(i + 1).padStart(2, '0')}
              </div>
              {/* 🔴 ONE FLOWING BLOCK, not two fixed offsets. The PowerPoint pinned
                  the head at +1.1% and the body at +5.3% — 45px apart — which only
                  works while the heading is one line. The headline-led headings are
                  longer ("COMPLAINTS GO PUBLIC BEFORE OWNERS CAN RESPOND"), wrap to
                  two lines, and used to run straight into the body text. The body
                  now sits BELOW the heading whatever height it grows to. */}
              <div style={{ position: 'absolute', left: PX(cx + 3.3), top: PY(ry + 1.1), width: P(19.4) }}>
                <div style={{ color: slide?.card_head_color || INK.cardHead, fontFamily: t.headFont,
                              fontWeight: 800, fontSize: pt(12) * headScale, lineHeight: 1.2 }}>
                  {String(bo.head || bo.label || '')}
                </div>
                {bo.text ? (
                  <div style={{ marginTop: pt(6),
                                color: slide?.card_body_color || INK.cardText, fontFamily: t.bodyFont,
                                fontWeight: 400, fontSize: pt(12) * bodyScale, lineHeight: 1.34 }}>
                    {String(bo.text)}
                  </div>
                ) : null}
              </div>
            </React.Fragment>
          );
        })}

        {r.body ? (
          <div style={{ position: 'absolute', left: PX(4.7), top: PY(90.3), width: P(56.2),
                        color: INK.cardText, fontFamily: t.bodyFont, fontSize: pt(13.5), lineHeight: 1.3 }}>
            {r.body}
          </div>
        ) : null}
      </div>
    );
  } else if (layout === 'cards') {
    /**
     * 🔴 CARDS — the same bullets, rendered as boxes instead of a list.
     *
     * Patrick 2026-08-12: "instead of boring bullet points cant we have rounded
     * boxes rather with the header inside the top outline of the box and then the
     * text inside and then have an array of 2 columns of those boxes."
     *
     * NOTHING CONVERTS. A card reads `head` (the label on the outline) and `text`
     * (the body) off the SAME bullet object a list uses, so switching the layout
     * back to `bullets` restores the list exactly. That is the whole reason this is
     * a layout and not a new content type — you can flip between them to see which
     * reads better from the back of a room, and never lose anything either way.
     *
     * The header sits ON the top border (a fieldset legend), which only works while
     * it stays 2-4 words — hence the size control rather than auto-shrink.
     */
    // 1, 2 or 3 across. Three is genuinely useful for 6-9 short cards — it turns
    // 3 deep rows into 2 shallow ones — but the body text has to shrink with it.
    const nCols = Number(slide?.bullet_columns);
    const cardCols = nCols === 1 ? 1 : nCols === 3 ? 3 : 2;
    const headSize = px(1.9, 1.9) * (Number(slide?.card_head_scale) > 0 ? Number(slide.card_head_scale) : 1);
    const cardBg = slide?.card_bg || 'transparent';
    const headColor = slide?.card_head_color || accent;
    const renderCard = (bo, i, offset) => {
            const head = (bo.head || bo.label || '').trim();
            return (
              <div key={i} style={{
                gridColumn: offset ? `${offset} / span 2` : 'span 2',
                position: 'relative', border: `2px solid ${slide?.card_line || accent}`,
                borderRadius: px(1.3, 1.3), background: cardBg,
                padding: `${head ? px(2.1, 2.1) : px(1.4, 1.4)}px ${px(1.4, 1.4)}px ${px(1.4, 1.4)}px`,
                // 🔴 overflow VISIBLE. The header sits ON the border via
                // translateY(-52%), i.e. deliberately outside the box — so clipping
                // the card cuts the header in half. The TEXT is clipped instead, on
                // the inner div below, which is what actually needs bounding.
                display: 'flex', alignItems: 'flex-start', minHeight: 0, overflow: 'visible',
              }}>
                {head ? (
                  <div style={{
                    position: 'absolute', top: 0, left: px(1.2, 1.2), transform: 'translateY(-52%)',
                    background: cardBg === 'transparent' ? t.bg : cardBg, padding: `0 ${px(0.7, 0.7)}px`,
                    fontFamily: t.headFont, fontWeight: 800, fontSize: headSize,
                    color: headColor, letterSpacing: '0.01em', whiteSpace: 'nowrap',
                    maxWidth: '92%', overflow: 'hidden', textOverflow: 'ellipsis',
                  }}>{head}</div>
                ) : null}
                {/* card body sizes and colours INDEPENDENTLY of the slide body —
                    they are different jobs: one is a caption inside a box, the other
                    is a sentence under the grid. */}
                <div style={{ ...bodyStyle, lineHeight: 1.3, width: '100%', overflow: 'hidden',
                              fontSize: px(1.75, 1.75) * (Number(slide?.card_body_scale) > 0 ? Number(slide.card_body_scale) : 1),
                              // 🔴 falls back to the theme BODY colour, not `ink`
                              // (the headline colour) — otherwise changing the theme
                              // headline silently restyled every card body.
                              color: slide?.card_body_color || t.inkSoft }}>
                  {bo.text}
                </div>
              </div>
            );
    };
    content = (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: vJustify, gap: px(2, 2) }}>
        <Eyebrow />
        {r.headline ? <h1 style={{ ...headStyle, fontSize: px(Number(t.headSize) * 0.8, 5.7) * fit.head }}>{r.headline}</h1> : null}
        {r.subhead ? <p style={{ ...subStyle, fontSize: px(Number(t.subSize) * 0.82, 2.8) * fit.body }}>{r.subhead}</p> : null}
        {/* 🔴 flex:1 + stretch — the grid claims the leftover height and the rows
            SHARE it, so cards come out rectangular. With alignContent:'start' every
            row collapsed to its text height and eight cards read as flat strips.
            Eight is still too many for 16:9: 2 columns x 4 rows leaves ~150px a row.
            Six is the comfortable ceiling. */}
        {/* 🔴 SUB-GRID SO CENTRING IS POSSIBLE. 5 cards on 3 columns needs the last
            row shifted by HALF a column — which a plain 3-column grid cannot express,
            so the first attempt silently did nothing. Doubling the tracks and giving
            every card span-2 makes a half-column shift a whole sub-column. */}
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cardCols * 2}, 1fr)`,
                      gap: px(1.6, 1.6), marginTop: px(0.8, 0.8),
                      flex: 1, minHeight: 0, alignContent: 'stretch' }}>
          {(() => {
            // 🔴 CENTRE AN INCOMPLETE LAST ROW. 5 cards on a 3-column grid leaves a
            // hole in column 3, which reads as a mistake rather than a choice. This
            // pushes the orphans to the middle so 4, 5, 7 and 8 all sit deliberately
            // instead of only exact multiples of the column count. Off by default —
            // it changes existing slides, so it is opt-in per slide.
            const items = activeBulletObjects(slide);
            const rem = items.length % cardCols;
            // in SUB-columns: the orphan row occupies rem*2, so the left gap is
            // (cardCols*2 - rem*2)/2 sub-columns; +1 because grid lines are 1-based.
            const orphanOffset = (slide?.card_center_last && rem !== 0)
              ? ((cardCols * 2) - (rem * 2)) / 2 + 1 : 0;
            return items.map((bo, i) => {
              const isFirstOrphan = orphanOffset > 0 && i === items.length - rem;
              return renderCard(bo, i, isFirstOrphan ? orphanOffset : 0);
            });
          })()}
        </div>
        {/* 🔴 BODY. The cards layout rendered headline, subhead and the grid and
            NOTHING ELSE, so switching a slide to cards made its body text disappear
            from the canvas while still sitting in the database. */}
        {/* 🔴 SAME STYLING AS THE STATEMENT LAYOUT. I had overridden the colour to
            inkSoft here, so switching a slide to cards silently restyled its body
            text from the theme colour to muted grey. Layout should change the
            arrangement, never the palette. */}
        {r.body ? <p style={{ ...bodyStyle, maxWidth: '86%', marginTop: px(1, 1) }}>{r.body}</p> : null}
      </div>
    );
  } else {
    // statement — the default and the most-used
    content = (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: vJustify, gap: px(2.2, 2.2) }}>
        <Eyebrow />
        <h1 style={headStyle}>{r.headline}</h1>
        {r.subhead ? <p style={subStyle}>{r.subhead}</p> : null}
        {r.body ? <p style={{ ...bodyStyle, maxWidth: '86%' }}>{r.body}</p> : null}
        {bul.length ? <Bullets items={bul} /> : null}
        {slide?.stats?.length ? <div style={{ marginTop: px(1.2, 1.2) }}><Stats /></div> : null}
        <div style={{ marginTop: 'auto' }}>
          <div style={{ height: 6, width: px(7, 7), background: accent, borderRadius: 4 }} />
        </div>
      </div>
    );
  }

  return (
    <div
      className={className}
      style={{ width, height: width * (H / W), position: 'relative', overflow: 'hidden', borderRadius: 10, ...style }}
    >
      <div data-slide-root style={{
        width: W, height: H, position: 'absolute', top: 0, left: 0,
        transform: `scale(${scale})`, transformOrigin: 'top left',
        // 🔴 per-slide background overrides the theme, so one slide can break the
        // pattern (a title or a closer) without forking the whole theme.
        background: slide?.bg || t.bg, color: t.ink, overflow: 'hidden',
        padding: ['image','v8split'].includes(layout) ? 0 : `${padT}px ${padR}px ${padB}px ${padL}px`,
        boxSizing: 'border-box', display: 'block',
      }}>
        <div ref={contentRef} style={{ height: '100%' }}>{content}</div>

        {/* THE LOGO — same contract as the QR: absolutely positioned, never in the
            content flow, so it cannot push a card down or inflate the overflow
            measurement. Per-slide on/off with a deck-level default, because a logo on
            EVERY slide of a 5-minute deck is noise, but you want it on the opener and
            the closer without setting it twice. */}
        {(slide?.logo_enabled ?? deck?.logo_default) && (deck?.logo_url || '/pitch-logo-transparent.svg') ? (
          <img src={deck?.logo_url || '/pitch-logo-transparent.svg'} alt="" draggable={false}
            onPointerDown={(e) => {
              // 🔴 DRAGGABLE, same contract as the QR. Corner presets alone were not
              // enough — a logo usually needs nudging away from a headline, not
              // snapping to a corner.
              if (!onLogoMove) return;
              e.preventDefault(); e.stopPropagation();
              const host = e.currentTarget.parentElement.getBoundingClientRect();
              const move = (ev) => {
                const x = Math.max(0, Math.min(92, ((ev.clientX - host.left) / host.width) * 100));
                const y = Math.max(0, Math.min(92, ((ev.clientY - host.top) / host.height) * 100));
                onLogoMove(Math.round(x * 10) / 10, Math.round(y * 10) / 10);
              };
              const up = () => {
                window.removeEventListener('pointermove', move);
                window.removeEventListener('pointerup', up);
              };
              window.addEventListener('pointermove', move);
              window.addEventListener('pointerup', up);
            }}
            style={{
              position: 'absolute',
              /* CON-1632 — explicit stacking. The `image` and `v8split` layouts
                 paint a full-bleed picture in an absolutely-positioned layer; DOM
                 order alone put the logo above it, but any future layout that sets
                 its own z-index would silently bury it. Cheap insurance against
                 exactly the class of bug that lost the QR. */
              zIndex: 40,
              left: `${slide?.logo_x != null ? Number(slide.logo_x) : 4}%`,
              top: `${slide?.logo_y != null ? Number(slide.logo_y) : 4}%`,
              width: px(Number(slide?.logo_size) > 0 ? Number(slide.logo_size) : 9, 9),
              height: 'auto', userSelect: 'none',
              cursor: onLogoMove ? 'grab' : 'default',
              pointerEvents: onLogoMove ? 'auto' : 'none',
            }} />
        ) : null}

        {/* THE QR — floating, positioned, draggable.
            🔴 It used to sit IN the flow, so it consumed vertical space and
            counted toward the overflow even when the right-hand third of the
            slide was empty. Patrick: "there's always enough space on the right,
            you should adjust that." Now absolutely positioned, so it neither
            pushes content down nor inflates the below-the-fold number — and a
            stored x/y makes dragging it mean something. `closer` keeps its own
            centred, enormous QR, because there the code IS the slide. */}
        {qrOn && layout !== 'closer' ? (
          <div
            onPointerDown={(e) => {
              if (!onQrMove) return;
              e.preventDefault(); e.stopPropagation();
              const host = e.currentTarget.parentElement.getBoundingClientRect();
              const move = (ev) => {
                // clamp so it can never be dragged off the canvas
                const x = Math.max(2, Math.min(88, ((ev.clientX - host.left) / host.width) * 100));
                const y = Math.max(2, Math.min(86, ((ev.clientY - host.top) / host.height) * 100));
                onQrMove(Math.round(x * 10) / 10, Math.round(y * 10) / 10);
              };
              const up = () => {
                window.removeEventListener('pointermove', move);
                window.removeEventListener('pointerup', up);
              };
              window.addEventListener('pointermove', move);
              window.addEventListener('pointerup', up);
            }}
            style={{
              position: 'absolute',
              zIndex: 45,   // CON-1632 — always above every layout layer
              left: `${slide?.qr_x ?? 76}%`,
              top: `${slide?.qr_y ?? 68}%`,
              display: 'flex', alignItems: 'center', gap: 20,
              cursor: onQrMove ? 'grab' : 'default',
              touchAction: 'none',
            }}
          >
            <div style={{ background: t.qrBg, padding: 14, borderRadius: 16, lineHeight: 0 }}>
              <QRCodeSVG value={qrUrl} size={Number(slide?.qr_size) || 180} level="M"
                bgColor={t.qrBg} fgColor={t.qrInk} />
            </div>
            {qrCap ? (
              <div style={{ fontFamily: t.bodyFont, fontSize: px(1.5, 1.5), lineHeight: 1.3,
                            color: t.inkFaint, maxWidth: 380, fontWeight: 600 }}>{qrCap}</div>
            ) : null}
          </div>
        ) : null}

        {/* THE CUT LINE — where the projector stops. EDITOR ONLY: gated on the
            explicit showOverflow prop, which only the editing canvas passes. It
            was previously gated on showQr (default true), which put it on the
            audience screen, the share link, rehearsal playback and the PDF. */}
        {overflowPx > 0 && showOverflow ? (
          <>
            <div style={{
              position: 'absolute', left: 0, right: 0, bottom: 0, height: 4,
              background: '#ef4444', opacity: 0.9,
            }} />
            <div style={{
              position: 'absolute', right: 18, bottom: 16, background: '#ef4444', color: '#fff',
              padding: '8px 16px', borderRadius: 8, fontFamily: 'system-ui, sans-serif',
              fontSize: 26, fontWeight: 800, letterSpacing: '-0.01em',
            }}>
              ↓ {overflowPx}px below the fold — this will NOT be seen
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
